import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { Position } from '@wheere/api-tracker';

/**
 * V7 integrator position-history viewer. Shows a list/timeline of positions with a date-range
 * slider; the actual map rendering is delegated to the parent (the integrator detail page wires
 * a MapLibre map alongside this control).
 *
 * <p>Implementation note: this is a thin shell — exposes {@code positions} and a slider index
 * so the parent can highlight the current position on its own map. Once the design is locked,
 * embed an inline mini-map here directly.
 */
@Component({
  selector: 'lib-position-history',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <header class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-gray-700">Position history</h3>
        <span class="text-xs text-gray-500">{{ positions().length }} fixes</span>
      </header>

      @if (positions().length > 0) {
        <input
          type="range"
          class="w-full"
          [min]="0"
          [max]="positions().length - 1"
          [value]="cursor()"
          (input)="onCursorInput($event)" />

        <div class="rounded border border-gray-200 bg-gray-50 p-2 text-xs">
          @if (currentPosition(); as p) {
            <div class="flex flex-wrap gap-x-4 gap-y-1 font-mono text-gray-700">
              <span><b>lat</b> {{ p.latitude?.toFixed(6) }}</span>
              <span><b>lon</b> {{ p.longitude?.toFixed(6) }}</span>
              <span><b>alt</b> {{ p.altitude?.toFixed(1) }} m</span>
              <span class="text-gray-500">{{ formatDate(p.timestamp) }}</span>
            </div>
          }
        </div>
      } @else {
        <p class="text-sm text-gray-500">No positions in this range.</p>
      }
    </div>
  `,
})
export class PositionHistoryComponent {
  positions = input.required<Position[]>();

  protected readonly cursor = signal(0);

  protected readonly currentPosition = computed<Position | null>(() => {
    const list = this.positions();
    if (!list.length) return null;
    const i = Math.min(this.cursor(), list.length - 1);
    return list[i] ?? null;
  });

  protected onCursorInput(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.cursor.set(value);
  }

  protected formatDate(d: Date | string | undefined): string {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return String(d);
    return date.toLocaleString();
  }
}
